import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash2, Plus, LogOut, Upload, CalendarIcon, Megaphone, ChevronDown, Copy, Users } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";
import PageSeo from "@/components/PageSeo";
import type { Tables } from "@/integrations/supabase/types";

type Event = Tables<"events">;

interface EventForm {
  title: string;
  date: string;
  time: string;
  dj: string;
  location: string;
  description: string;
  image_url: string;
  sort_order: number;
  is_active: boolean;
  link_url: string;
  link_text: string;
}

const emptyForm: EventForm = {
  title: "",
  date: "",
  time: "",
  dj: "",
  location: "",
  description: "",
  image_url: "",
  sort_order: 0,
  is_active: true,
  link_url: "",
  link_text: "",
};

const AdminEvents = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [form, setForm] = useState<EventForm>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Announcement state
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const [announcementMessage, setAnnouncementMessage] = useState("");
  const [announcementActive, setAnnouncementActive] = useState(false);
  const [announcementId, setAnnouncementId] = useState<string | null>(null);
  const [announcementLoading, setAnnouncementLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) navigate("/admin", { replace: true });
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) navigate("/admin", { replace: true });
    });

    fetchEvents();
    fetchAnnouncement();
    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchAnnouncement = async () => {
    const { data } = await supabase
      .from("site_announcements")
      .select("*")
      .limit(1)
      .maybeSingle();
    if (data) {
      setAnnouncementId(data.id);
      setAnnouncementTitle(data.title);
      setAnnouncementMessage(data.message);
      setAnnouncementActive(data.is_active);
    }
  };

  const saveAnnouncement = async () => {
    setAnnouncementLoading(true);
    try {
      if (announcementId) {
        const { error } = await supabase
          .from("site_announcements")
          .update({ title: announcementTitle, message: announcementMessage, is_active: announcementActive })
          .eq("id", announcementId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("site_announcements")
          .insert({ title: announcementTitle, message: announcementMessage, is_active: announcementActive });
        if (error) throw error;
      }
      toast({ title: "Ankündigung gespeichert" });
      fetchAnnouncement();
    } catch (error: any) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    } finally {
      setAnnouncementLoading(false);
    }
  };

  const fetchEvents = async () => {
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .order("date", { ascending: true })
      .order("time", { ascending: true, nullsFirst: true });
    if (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    } else {
      setEvents(data || []);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const fileExt = file.name.split(".").pop();
    const fileName = `${crypto.randomUUID()}.${fileExt}`;

    const { error } = await supabase.storage
      .from("event-images")
      .upload(fileName, file);

    if (error) {
      toast({ title: "Upload fehlgeschlagen", description: error.message, variant: "destructive" });
    } else {
      const { data: urlData } = supabase.storage
        .from("event-images")
        .getPublicUrl(fileName);
      setForm((prev) => ({ ...prev, image_url: urlData.publicUrl }));
      toast({ title: "Bild hochgeladen" });
    }
    setUploading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        const { error } = await supabase.from("events").update(form).eq("id", editingId);
        if (error) throw error;
        toast({ title: "Event aktualisiert" });
      } else {
        const { error } = await supabase.from("events").insert(form);
        if (error) throw error;
        toast({ title: "Event erstellt" });
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      fetchEvents();
    } catch (error: any) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event: Event) => {
    setForm({
      title: event.title,
      date: event.date,
      time: event.time || "",
      dj: event.dj || "",
      location: event.location || "",
      description: event.description || "",
      image_url: event.image_url || "",
      sort_order: event.sort_order || 0,
      is_active: event.is_active ?? true,
      link_url: (event as any).link_url || "",
      link_text: (event as any).link_text || "",
    });
    setEditingId(event.id);
    setShowForm(false);
  };

  const handleDuplicate = (event: Event) => {
    setForm({
      title: event.title + " (Kopie)",
      date: event.date,
      time: event.time || "",
      dj: event.dj || "",
      location: event.location || "",
      description: event.description || "",
      image_url: event.image_url || "",
      sort_order: event.sort_order || 0,
      is_active: event.is_active ?? true,
      link_url: (event as any).link_url || "",
      link_text: (event as any).link_text || "",
    });
    setEditingId(null);
    setShowForm(true);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Event wirklich löschen?")) return;
    const { error } = await supabase.from("events").delete().eq("id", id);
    if (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Event gelöscht" });
      fetchEvents();
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin");
  };

  const updateField = (field: keyof EventForm, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      <PageSeo
        title="Events verwalten – Luther Club Admin"
        description="Interner Adminbereich zum Verwalten der Events des Luther Club Kaiserslautern."
        path="/admin/events"
        noindex
      />
      <header className="border-b border-border px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-2">
          <h1 className="text-base sm:text-xl font-semibold text-primary truncate">Events verwalten</h1>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/applications">
                <Users className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Bewerbungen</span>
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setForm(emptyForm);
                setEditingId(null);
                setShowForm(!showForm);
              }}
            >
              <Plus className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Neues Event</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8 space-y-6 sm:space-y-8">
        {/* Announcement Section */}
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Megaphone className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-medium text-primary">Aktuelle Ankündigung</h2>
          </div>
          <Input
            placeholder="Titel der Ankündigung"
            value={announcementTitle}
            onChange={(e) => setAnnouncementTitle(e.target.value)}
          />
          <Textarea
            placeholder="Nachricht / Text"
            value={announcementMessage}
            onChange={(e) => setAnnouncementMessage(e.target.value)}
            rows={3}
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-3 text-sm text-foreground/80 cursor-pointer">
              <Switch
                checked={announcementActive}
                onCheckedChange={(checked) => setAnnouncementActive(checked)}
              />
              {announcementActive ? "Ankündigung aktiv" : "Ankündigung deaktiviert"}
            </label>
            <Button onClick={saveAnnouncement} disabled={announcementLoading} size="sm">
              {announcementLoading ? "..." : "Speichern"}
            </Button>
          </div>
        </div>

        {showForm && !editingId && (
          <EventFormCard
            form={form}
            updateField={updateField}
            handleSubmit={handleSubmit}
            handleImageUpload={handleImageUpload}
            fileInputRef={fileInputRef}
            uploading={uploading}
            loading={loading}
            editingId={editingId}
            onCancel={() => {
              setShowForm(false);
              setEditingId(null);
              setForm(emptyForm);
            }}
          />
        )}

        {(() => {
          const today = new Date().toISOString().split("T")[0];
          const upcoming = events.filter((e) => e.date >= today);
          const past = events.filter((e) => e.date < today);

          const renderEventRow = (event: Event) => {
            const isEditing = editingId === event.id;
            return (
              <div
                key={event.id}
                className={`bg-card border border-border rounded-lg overflow-hidden ${
                  !event.is_active ? "opacity-50" : ""
                } ${isEditing ? "ring-2 ring-primary/40" : ""}`}
              >
                <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4">
                  {event.image_url && (
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="w-12 h-12 sm:w-16 sm:h-16 rounded object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-primary text-sm sm:text-base leading-snug break-words line-clamp-2">
                      {event.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-foreground/60 mt-1 break-words">
                      {new Date(event.date + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}{event.time && ` · ${event.time}`}{event.dj && ` · ${event.dj}`}
                    </p>
                  </div>
                  <div className="flex gap-0.5 sm:gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-10 sm:w-10" onClick={() => handleDuplicate(event)} title="Duplizieren">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant={isEditing ? "secondary" : "ghost"}
                      size="icon"
                      className="h-8 w-8 sm:h-10 sm:w-10"
                      onClick={() => {
                        if (isEditing) {
                          setEditingId(null);
                          setForm(emptyForm);
                        } else {
                          handleEdit(event);
                        }
                      }}
                      title={isEditing ? "Bearbeitung schließen" : "Bearbeiten"}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-10 sm:w-10" onClick={() => handleDelete(event.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>

                {isEditing && (
                  <div className="border-t border-border p-3 sm:p-4 bg-background/40">
                    <EventFormCard
                      form={form}
                      updateField={updateField}
                      handleSubmit={handleSubmit}
                      handleImageUpload={handleImageUpload}
                      fileInputRef={fileInputRef}
                      uploading={uploading}
                      loading={loading}
                      editingId={editingId}
                      embedded
                      onCancel={() => {
                        setEditingId(null);
                        setForm(emptyForm);
                      }}
                    />
                  </div>
                )}
              </div>
            );
          };

          return (
            <>
              <div className="space-y-3">
                <h2 className="text-lg font-medium text-primary">Kommende Events</h2>
                {upcoming.length === 0 && (
                  <p className="text-center text-foreground/50 py-8">Keine kommenden Events.</p>
                )}
                {upcoming.map(renderEventRow)}
              </div>

              {past.length > 0 && (
                <Collapsible className="mt-8">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 text-foreground/50 hover:text-foreground/70 px-0">
                      <ChevronDown className="w-4 h-4 transition-transform [[data-state=open]>&]:rotate-180" />
                      <h2 className="text-lg font-medium">Vergangene Events ({past.length})</h2>
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-3 mt-3">
                    {past.map((event) => (
                      <div key={event.id} className="opacity-50">
                        {renderEventRow(event)}
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

interface EventFormCardProps {
  form: EventForm;
  updateField: (field: keyof EventForm, value: string | number | boolean) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  uploading: boolean;
  loading: boolean;
  editingId: string | null;
  onCancel: () => void;
  embedded?: boolean;
}

const EventFormCard = ({
  form,
  updateField,
  handleSubmit,
  handleImageUpload,
  fileInputRef,
  uploading,
  loading,
  editingId,
  onCancel,
  embedded = false,
}: EventFormCardProps) => {
  return (
    <form
      onSubmit={handleSubmit}
      className={
        embedded
          ? "space-y-4"
          : "bg-card border border-border rounded-lg p-4 sm:p-6 space-y-4"
      }
    >
      {!embedded && (
        <h2 className="text-lg font-medium text-primary">
          {editingId ? "Event bearbeiten" : "Neues Event"}
        </h2>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          placeholder="Titel *"
          value={form.title}
          onChange={(e) => updateField("title", e.target.value)}
          required
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !form.date && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {form.date
                ? new Date(form.date + "T00:00:00").toLocaleDateString("de-DE", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : "Datum wählen *"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={form.date ? new Date(form.date + "T00:00:00") : undefined}
              onSelect={(day) => {
                if (day) updateField("date", format(day, "yyyy-MM-dd"));
              }}
              locale={de}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
        <Input
          placeholder="Uhrzeit (z.B. 22:00)"
          value={form.time}
          onChange={(e) => updateField("time", e.target.value)}
        />
        <Input
          placeholder="DJ"
          value={form.dj}
          onChange={(e) => updateField("dj", e.target.value)}
        />
        <Input
          placeholder="Location"
          value={form.location}
          onChange={(e) => updateField("location", e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-foreground/80">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => updateField("is_active", e.target.checked)}
          />
          Aktiv
        </label>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 mr-1" />
            {uploading ? "Lädt hoch..." : "Bild hochladen"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
        </div>
        {form.image_url && (
          <img
            src={form.image_url}
            alt="Vorschau"
            className="w-32 h-20 object-cover rounded border border-border"
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          placeholder="Link URL (optional)"
          value={form.link_url}
          onChange={(e) => updateField("link_url", e.target.value)}
        />
        <Input
          placeholder="Link Text (z.B. 'Tickets kaufen')"
          value={form.link_text}
          onChange={(e) => updateField("link_text", e.target.value)}
        />
      </div>

      <Textarea
        placeholder="Beschreibung"
        value={form.description}
        onChange={(e) => updateField("description", e.target.value)}
        rows={3}
      />

      <div className="flex gap-2">
        <Button type="submit" disabled={loading}>
          {loading ? "..." : editingId ? "Speichern" : "Erstellen"}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
};

export default AdminEvents;
